import moment from 'moment';

const getSortValue = (f, k) => {
  switch (k) {
    case 'estimated':
      return (f.overrides && f.overrides.estimated) ? f.overrides.estimated : f.estimated;
    case 'scheduled':
      return f.scheduled;
    case 'sdtDate':
      return f.scheduled;
    case 'gate':
      return (f.overrides && f.overrides.gate) ? f.overrides.gate : f.gate;
    case 'claim':
      return (f.overrides && f.overrides.claim) ? f.overrides.claim : f.claim;
    case 'status':
      return (f.overrides && f.overrides.status) ? f.overrides.status : f.status;
    case 'airline':
      return f.airlineName || f.airline;
    default:
      return f[k];
  }
};

const safeStr = (v) => (v == null ? '' : String(v));
const cmpString = (a, b) => safeStr(a).toLowerCase().localeCompare(safeStr(b).toLowerCase());
const cmpAlphaNum = (a, b) => safeStr(a).localeCompare(safeStr(b), undefined, { numeric: true, sensitivity: 'base' });

const toMinutes = (s) => {
  if (s == null) return Number.POSITIVE_INFINITY;
  const v = String(s).trim();
  if (!v) return Number.POSITIVE_INFINITY;
  const tm = moment(v, ['HH:mm', 'HH:mm:ss', 'h:mm a', 'hh:mm a'], true);
  if (tm.isValid()) return tm.hours() * 60 + tm.minutes() + tm.seconds() / 60;
  const md = moment(v, [
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD HH:mm',
    'YYYY-MM-DD hh:mm a',
    moment.ISO_8601
  ], true);
  if (md.isValid()) return md.hours() * 60 + md.minutes() + md.seconds() / 60;
  const t = Date.parse(v);
  if (Number.isFinite(t)) {
    const d = new Date(t);
    return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;
  }
  return Number.POSITIVE_INFINITY;
};

const cmpDate = (a, b) => toMinutes(a) - toMinutes(b);

const toDay = (s) => {
  if (s == null) return Number.POSITIVE_INFINITY;
  const v = String(s).trim();
  if (!v) return Number.POSITIVE_INFINITY;
  const md = moment(v, [
    'YYYY-MM-DD',
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD HH:mm',
    'YYYY-MM-DD hh:mm a',
    moment.ISO_8601
  ], true);
  if (md.isValid()) return md.startOf('day').valueOf();
  const t = Date.parse(v);
  if (Number.isFinite(t)) {
    const d = new Date(t);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }
  return Number.POSITIVE_INFINITY;
};

const cmpDateOnly = (a, b) => toDay(a) - toDay(b);

/**
 * Single-pass partition by flight type (O(n), reused on tab switch).
 */
export function partitionFlightsByType(flights) {
  if (!Array.isArray(flights) || !flights.length) {
    return { all: flights || [], departure: [], arrival: [] };
  }
  const departure = [];
  const arrival = [];
  for (let i = 0; i < flights.length; i++) {
    const f = flights[i];
    const t = f.flightType && String(f.flightType).toLowerCase();
    if (t === 'departure') departure.push(f);
    else if (t === 'arrival') arrival.push(f);
  }
  return { all: flights, departure, arrival };
}

/**
 * Precomputed numeric/string sort keys per flight id (parse dates once).
 */
export function buildSortMetaMap(flights) {
  const map = Object.create(null);
  if (!Array.isArray(flights)) return map;
  for (let i = 0; i < flights.length; i++) {
    const f = flights[i];
    if (f && f.id != null) {
      map[f.id] = buildFlightSortMeta(f);
    }
  }
  return map;
}

export function buildFlightSortMeta(f) {
  const scheduled = f.scheduled;
  const estimated = (f.overrides && f.overrides.estimated) ? f.overrides.estimated : f.estimated;
  const city = f.airportCity || f.city;
  return {
    id: safeStr(f.id).toLowerCase(),
    airline: safeStr(f.airlineName || f.airline).toLowerCase(),
    airlineCode: safeStr(f.airlineCode).toLowerCase(),
    flightNumber: safeStr(f.flightNumber),
    flightType: safeStr(f.flightType).toLowerCase(),
    city: safeStr(city).toLowerCase(),
    status: safeStr((f.overrides && f.overrides.status) ? f.overrides.status : f.status).toLowerCase(),
    claim: safeStr((f.overrides && f.overrides.claim) ? f.overrides.claim : f.claim).toLowerCase(),
    gate: safeStr((f.overrides && f.overrides.gate) ? f.overrides.gate : f.gate),
    scheduledMin: toMinutes(scheduled),
    estimatedMin: toMinutes(estimated),
    sdtDay: toDay(scheduled),
  };
}

/**
 * Sort using precomputed meta (no moment calls during compare).
 */
export function sortFlightsFast(flights, key, metaMap) {
  if (!key || !Array.isArray(flights) || !flights.length) return flights || [];

  const meta = (f) => metaMap[f.id] || buildFlightSortMeta(f);

  const cmpMetaStr = (a, b, field) => meta(a)[field].localeCompare(meta(b)[field]);
  const cmpMetaNum = (a, b, field) => {
    const va = meta(a)[field];
    const vb = meta(b)[field];
    if (va < vb) return -1;
    if (va > vb) return 1;
    return 0;
  };

  const arr = flights.slice();

  switch (key) {
    case 'id':
    case 'airline':
    case 'airlineCode':
    case 'flightType':
    case 'city':
    case 'status':
    case 'claim':
      arr.sort((a, b) => cmpMetaStr(a, b, key === 'airline' ? 'airline' : key));
      return arr;
    case 'flightNumber':
      arr.sort((a, b) => meta(a).flightNumber.localeCompare(meta(b).flightNumber, undefined, { numeric: true, sensitivity: 'base' }));
      return arr;
    case 'gate':
      arr.sort((a, b) => cmpMetaStr(a, b, 'gate'));
      return arr;
    case 'scheduled':
      arr.sort((a, b) => cmpMetaNum(a, b, 'scheduledMin'));
      return arr;
    case 'estimated':
      arr.sort((a, b) => cmpMetaNum(a, b, 'estimatedMin'));
      return arr;
    case 'sdtDate':
      arr.sort((a, b) => {
        const primary = cmpMetaNum(a, b, 'sdtDay');
        if (primary !== 0) return primary;
        return cmpMetaNum(a, b, 'estimatedMin');
      });
      return arr;
    default:
      return arr;
  }
}

/**
 * Sort flights by column key. Returns a new array (legacy path).
 */
export function sortFlights(flights, key) {
  if (!key || !Array.isArray(flights)) return flights;

  switch (key) {
    case 'id':
    case 'airline':
    case 'airlineCode':
    case 'flightType':
    case 'city':
    case 'status':
    case 'claim':
      return flights.slice().sort((a, b) => cmpString(getSortValue(a, key), getSortValue(b, key)));
    case 'flightNumber':
    case 'gate':
      return flights.slice().sort((a, b) => cmpAlphaNum(getSortValue(a, key), getSortValue(b, key)));
    case 'scheduled':
    case 'estimated':
      return flights.slice().sort((a, b) => cmpDate(getSortValue(a, key), getSortValue(b, key)));
    case 'sdtDate': {
      const arr = flights.slice();
      arr.sort((a, b) => {
        const primary = cmpDateOnly(getSortValue(a, 'sdtDate'), getSortValue(b, 'sdtDate'));
        if (primary !== 0) return primary;
        return cmpDate(getSortValue(a, 'estimated'), getSortValue(b, 'estimated'));
      });
      return arr;
    }
    default:
      return flights;
  }
}

/**
 * Filter flights by search query (id, airline name/code, flight number).
 */
export function searchFlights(flights, query) {
  if (!query || !Array.isArray(flights)) return flights;
  const q = query.toLowerCase();
  const out = [];
  for (let i = 0; i < flights.length; i++) {
    const flight = flights[i];
    const idMatch = flight.id && flight.id.toLowerCase().startsWith(q);
    const airlineName = (flight.airlineName || flight.airline || '').toLowerCase();
    const airlineMatch = airlineName.includes(q);
    const codeMatch = flight.airlineCode && flight.airlineCode.toLowerCase().includes(q);
    const numMatch = flight.flightNumber && flight.flightNumber.toLowerCase().startsWith(q);
    if (idMatch || airlineMatch || codeMatch || numMatch) {
      out.push(flight);
    }
  }
  return out;
}

/**
 * Filter by departure / arrival / all tab.
 */
export function filterFlightsByType(flights, filter) {
  if (!filter || filter === 'all' || !Array.isArray(flights)) return flights;
  return flights.filter((flight) => {
    return flight.flightType && flight.flightType.toLowerCase() === filter;
  });
}

/**
 * Pick partition for tab (O(1) after partitionFlightsByType).
 */
export function getPartitionList(partitions, filter) {
  if (!partitions) return [];
  if (filter === 'departure') return partitions.departure;
  if (filter === 'arrival') return partitions.arrival;
  return partitions.all;
}
